package com.arena_web.utils

import kotlinx.browser.window

object LeafletMap {

    /**
     * Create an interactive address-picker map with a FIXED centre pin.
     * The pin stays pinned to the centre; the user pans the map underneath it to
     * position it, and pinch / scroll / +- buttons zoom for precision. Each
     * pan/zoom fires the move callback with the map's centre coordinates.
     */
    fun init(containerId: String, lat: Double, lng: Double, zoom: Int = 13) {
        window.asDynamic().__arenaMapContainerId = containerId
        window.asDynamic().__arenaMapLat = lat
        window.asDynamic().__arenaMapLng = lng
        window.asDynamic().__arenaMapZoom = zoom

        js("""
            (function() {
                var id = window.__arenaMapContainerId;
                var lat = window.__arenaMapLat;
                var lng = window.__arenaMapLng;
                var zoom = window.__arenaMapZoom;
                var container = document.getElementById(id);
                if (!container) { console.warn('[LeafletMap] container not found:', id); return; }
                if (typeof L === 'undefined') { console.warn('[LeafletMap] Leaflet not loaded'); return; }

                if (window.__arenaMap) {
                    try { window.__arenaMap.remove(); } catch(e) {}
                    window.__arenaMap = null;
                }

                // Remove old pin if exists
                var oldPin = document.getElementById(id + '-pin');
                if (oldPin) oldPin.remove();

                container.style.position = 'relative';

                // Fixed centre pin — a CSS overlay (pointer-events:none) so the
                // map pans under it; the centre is the selected point.
                var pin = document.createElement('div');
                pin.id = id + '-pin';
                pin.style.cssText = 'position:absolute;top:50%;left:50%;z-index:999;' +
                    'transform:translate(-50%,-100%);pointer-events:none;' +
                    'width:30px;height:40px;filter:drop-shadow(0 3px 6px rgba(0,0,0,0.4));';
                pin.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="30" height="40">' +
                    '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#0061FF"/>' +
                    '<circle cx="12" cy="11" r="5" fill="white"/>' +
                    '</svg>';
                container.appendChild(pin);

                // Keep wheel-zoom from scrolling the page. We deliberately do NOT
                // stop `touchmove`: Leaflet tracks drag moves via a document-level
                // listener, so stopping propagation here would block map panning on
                // touch. The host bottom sheet ignores map touches via its
                // `isInteractive` check (it treats `.leaflet-container` as a control).
                container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });

                var worldBounds = L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180));
                var map = L.map(id, {
                    zoomControl: true,
                    attributionControl: false,
                    scrollWheelZoom: true,
                    doubleClickZoom: true,
                    touchZoom: true,
                    boxZoom: true,
                    maxBounds: worldBounds,
                    maxBoundsViscosity: 1.0,
                    minZoom: 2,
                    worldCopyJump: true
                }).setView([lat, lng], zoom);

                L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                    maxZoom: 20,
                    subdomains: 'abcd',
                    noWrap: true,
                    bounds: worldBounds
                }).addTo(map);

                window.__arenaMap = map;
                window.__arenaMapSkipMove = false;

                // Pan / zoom moves the map under the fixed centre pin; report the
                // new centre as the selected point.
                map.on('moveend', function() {
                    if (window.__arenaMapSkipMove) {
                        window.__arenaMapSkipMove = false;
                        return;
                    }
                    var c = map.getCenter();
                    if (typeof window.__arenaMapMoveCallback === 'function') {
                        window.__arenaMapMoveCallback(c.lat, c.lng);
                    }
                });

                setTimeout(function() { map.invalidateSize(); }, 150);
            })();
        """)
    }

    /**
     * Center map on coordinates without triggering the move callback.
     * Used when autocomplete sets the location programmatically.
     */
    fun setView(lat: Double, lng: Double, zoom: Int = 15) {
        window.asDynamic().__arenaMapLat = lat
        window.asDynamic().__arenaMapLng = lng
        window.asDynamic().__arenaMapZoom = zoom

        js("""
            (function() {
                if (!window.__arenaMap) return;
                window.__arenaMapSkipMove = true;
                window.__arenaMap.setView(
                    [window.__arenaMapLat, window.__arenaMapLng],
                    window.__arenaMapZoom
                );
            })();
        """)
    }

    /**
     * Pan the browse map to a location (e.g. the selected city). Unlike
     * [setView] this uses `flyTo`-style `setView` with a city-level zoom and
     * does not touch the move-callback guard (the browse map has none).
     */
    fun panTo(lat: Double, lng: Double, zoom: Int = 12) {
        window.asDynamic().__arenaPanLat = lat
        window.asDynamic().__arenaPanLng = lng
        window.asDynamic().__arenaPanZoom = zoom
        js("""
            (function() {
                if (!window.__arenaMap) return;
                window.__arenaMap.setView(
                    [window.__arenaPanLat, window.__arenaPanLng],
                    window.__arenaPanZoom
                );
            })();
        """)
    }

    /**
     * Register a Kotlin callback for when the map center changes (user drag/zoom).
     */
    fun onMoveEnd(callback: (lat: Double, lng: Double) -> Unit) {
        window.asDynamic().__arenaMapMoveCallback = callback
    }

    /**
     * Create a read-only map for browsing markers (no center pin, no move callback).
     */
    fun initReadOnly(containerId: String, lat: Double, lng: Double, zoom: Int = 5) {
        window.asDynamic().__arenaMapContainerId = containerId
        window.asDynamic().__arenaMapLat = lat
        window.asDynamic().__arenaMapLng = lng
        window.asDynamic().__arenaMapZoom = zoom

        js("""
            (function() {
                var id = window.__arenaMapContainerId;
                var lat = window.__arenaMapLat;
                var lng = window.__arenaMapLng;
                var zoom = window.__arenaMapZoom;
                var container = document.getElementById(id);
                if (!container) { console.warn('[LeafletMap] container not found:', id); return; }
                if (typeof L === 'undefined') { console.warn('[LeafletMap] Leaflet not loaded'); return; }

                if (window.__arenaMap) {
                    try { window.__arenaMap.remove(); } catch(e) {}
                    window.__arenaMap = null;
                }

                container.style.position = 'relative';

                // Don't stop `touchmove` — Leaflet tracks drag/zoom via document
                // listeners, so stopping it here would block panning & pinch-zoom
                // (e.g. inside a bottom sheet). Wheel is kept so it doesn't scroll
                // the page.
                container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });

                var worldBounds = L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180));
                var map = L.map(id, {
                    zoomControl: false,
                    attributionControl: false,
                    scrollWheelZoom: true,
                    doubleClickZoom: true,
                    touchZoom: true,
                    boxZoom: true,
                    maxBounds: worldBounds,
                    maxBoundsViscosity: 1.0,
                    minZoom: 2,
                    // maxZoom on the MAP is required: with the raster basemap it
                    // came from the tile layer, but the MapLibre GL layer sets
                    // none, and L.markerClusterGroup throws "Map has no maxZoom
                    // specified" — which kills the whole init before any
                    // markers are added (no events/clubs on the map).
                    maxZoom: 20,
                    worldCopyJump: true
                }).setView([lat, lng], zoom);

                // Basemap = the EXACT mobile Arena navy style (see Arena_Mobile
                // docs/map-style.md): land #0e1626, buildings #101a2d, parks
                // #0f2233, water #060d1c, roads #1a2740/#213254/#2b3d66,
                // country borders #33456b; labels hidden except country + city
                // names. Raster tiles can't be recolored, so this is a MapLibre
                // GL vector layer (OpenFreeMap tiles, no API key) rendered
                // inside Leaflet via maplibre-gl-leaflet — markers/clustering
                // stay pure Leaflet. Falls back to CartoDB dark raster tiles if
                // the maplibre scripts didn't load.
                var isLightTheme = function() {
                    return document.documentElement.getAttribute('data-theme') === 'light';
                };
                if (typeof L.maplibreGL === 'function' && typeof maplibregl !== 'undefined') {
                    // Label language follows the app locale ('ua' app code =
                    // 'uk' in OpenMapTiles), falling back to the local name.
                    var appLang = 'en';
                    try { appLang = window.localStorage.getItem('language') || 'en'; } catch(e) {}
                    if (appLang === 'ua') appLang = 'uk';
                    var nameExpr = ['coalesce', ['get', 'name:' + appLang], ['get', 'name:en'], ['get', 'name']];

                    // Two palettes over the same layer skeleton: the exact
                    // mobile Arena navy (dark) and its daylight counterpart
                    // (light theme) — soft blue-grey land, pastel parks/water.
                    var mapPalette = function(light) {
                        return light ? {
                            bg: '#e7eef9', green: '#d6ebdd', water: '#c4d9f2',
                            building: '#dae3f0', roadMinor: '#cbd8ed',
                            roadMid: '#bccde8', roadMajor: '#a9bfe3',
                            boundary: '#a3b4d6', place: '#33415e',
                            country: '#5a6b8c', halo: '#ffffff'
                        } : {
                            bg: '#0e1626', green: '#0f2233', water: '#060d1c',
                            building: '#101a2d', roadMinor: '#1a2740',
                            roadMid: '#213254', roadMajor: '#2b3d66',
                            boundary: '#33456b', place: '#b4ccf6',
                            country: '#9fb3d4', halo: '#0a1120'
                        };
                    };
                    var buildArenaStyle = function(light) {
                        var c = mapPalette(light);
                        return {
                        version: 8,
                        glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
                        sources: {
                            omt: { type: 'vector', url: 'https://tiles.openfreemap.org/planet' }
                        },
                        layers: [
                            { id: 'bg', type: 'background', paint: { 'background-color': c.bg } },
                            { id: 'landuse-green', type: 'fill', source: 'omt', 'source-layer': 'landuse',
                              filter: ['in', 'class', 'grass', 'park', 'garden', 'pitch', 'cemetery', 'stadium', 'playground'],
                              paint: { 'fill-color': c.green } },
                            { id: 'park', type: 'fill', source: 'omt', 'source-layer': 'park',
                              paint: { 'fill-color': c.green } },
                            { id: 'water', type: 'fill', source: 'omt', 'source-layer': 'water',
                              paint: { 'fill-color': c.water } },
                            { id: 'waterway', type: 'line', source: 'omt', 'source-layer': 'waterway',
                              paint: { 'line-color': c.water, 'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 8, 0.5, 18, 6] } },
                            { id: 'building', type: 'fill', source: 'omt', 'source-layer': 'building', minzoom: 13,
                              paint: { 'fill-color': c.building } },
                            { id: 'road-minor', type: 'line', source: 'omt', 'source-layer': 'transportation', minzoom: 11,
                              filter: ['in', 'class', 'tertiary', 'minor', 'service'],
                              layout: { 'line-cap': 'round', 'line-join': 'round' },
                              paint: { 'line-color': c.roadMinor, 'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 12, 0.5, 14, 1.4, 18, 8] } },
                            { id: 'road-mid', type: 'line', source: 'omt', 'source-layer': 'transportation', minzoom: 8,
                              filter: ['in', 'class', 'primary', 'secondary'],
                              layout: { 'line-cap': 'round', 'line-join': 'round' },
                              paint: { 'line-color': c.roadMid, 'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 8, 0.5, 12, 1.4, 14, 2.5, 18, 10] } },
                            { id: 'road-major', type: 'line', source: 'omt', 'source-layer': 'transportation', minzoom: 5,
                              filter: ['in', 'class', 'motorway', 'trunk'],
                              layout: { 'line-cap': 'round', 'line-join': 'round' },
                              paint: { 'line-color': c.roadMajor, 'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 6, 0.8, 10, 1.5, 14, 4, 18, 14] } },
                            { id: 'boundary-country', type: 'line', source: 'omt', 'source-layer': 'boundary',
                              filter: ['all', ['==', 'admin_level', 2], ['!=', 'maritime', 1]],
                              paint: { 'line-color': c.boundary, 'line-width': 1 } },
                            { id: 'place-city', type: 'symbol', source: 'omt', 'source-layer': 'place', minzoom: 4,
                              filter: ['==', 'class', 'city'],
                              layout: { 'text-field': nameExpr, 'text-font': ['Noto Sans Regular'],
                                        'text-size': ['interpolate', ['linear'], ['zoom'], 4, 11, 10, 14, 14, 16] },
                              paint: { 'text-color': c.place, 'text-halo-color': c.halo, 'text-halo-width': 1.2 } },
                            { id: 'place-town', type: 'symbol', source: 'omt', 'source-layer': 'place', minzoom: 9,
                              filter: ['==', 'class', 'town'],
                              layout: { 'text-field': nameExpr, 'text-font': ['Noto Sans Regular'],
                                        'text-size': ['interpolate', ['linear'], ['zoom'], 9, 12, 14, 14] },
                              paint: { 'text-color': c.place, 'text-halo-color': c.halo, 'text-halo-width': 1.2 } },
                            { id: 'place-country', type: 'symbol', source: 'omt', 'source-layer': 'place', minzoom: 2, maxzoom: 10,
                              filter: ['==', 'class', 'country'],
                              layout: { 'text-field': nameExpr, 'text-font': ['Noto Sans Bold'],
                                        'text-size': ['interpolate', ['linear'], ['zoom'], 3, 11, 8, 14] },
                              paint: { 'text-color': c.country, 'text-halo-color': c.halo, 'text-halo-width': 1.2 } }
                        ]
                        };
                    };
                    var navyStyle = buildArenaStyle(isLightTheme());
                    // padding = extra canvas rendered around the viewport as a
                    // fraction of its size (binding default 0.1). During a
                    // 1-level zoom-out Leaflet CSS-scales the canvas by 0.5, so
                    // the rendered area covers (1+2p)*0.5 viewports — p=0.5 is
                    // exactly 1.0 (borderline: any cursor-anchored offset or a
                    // fast 2-notch wheel still exposes edges). 0.75 keeps a
                    // 25% reserve after the halving. fadeDuration:0 makes the
                    // areas MapLibre renders after the zoom appear instantly
                    // instead of fading in over 300ms — most of the perceived
                    // "edges loading in" was that fade.
                    var glLayer = L.maplibreGL({ style: navyStyle, padding: 0.75, fadeDuration: 0 }).addTo(map);
                    // Live theme switch: ThemeController dispatches
                    // 'arena-theme-changed' after flipping data-theme; swap the
                    // basemap style in place (markers/clusters are Leaflet
                    // layers and are unaffected).
                    var onThemeChange = function() {
                        try {
                            glLayer.getMaplibreMap().setStyle(buildArenaStyle(isLightTheme()));
                        } catch(e) {}
                    };
                    window.addEventListener('arena-theme-changed', onThemeChange);
                    map.on('remove', function() {
                        window.removeEventListener('arena-theme-changed', onThemeChange);
                    });
                } else {
                    L.tileLayer('https://{s}.basemaps.cartocdn.com/' + (isLightTheme() ? 'light_all' : 'dark_all') + '/{z}/{x}/{y}{r}.png', {
                        maxZoom: 20,
                        subdomains: 'abcd',
                        noWrap: true,
                        bounds: worldBounds,
                        updateWhenZooming: true,
                        keepBuffer: 6
                    }).addTo(map);
                }

                window.__arenaMap = map;
                // A fresh map hasn't framed its markers yet — the first
                // addMarkers() call fits the camera to them, refreshes don't.
                window.__arenaMarkersFitted = false;

                // Cluster group when the markercluster plugin is present, else a
                // plain layer group so the map still works. Custom cluster icon
                // = a blue disc with the child count; clustering kicks in when
                // zoomed out (individual pins at the default view).
                if (typeof L.markerClusterGroup === 'function') {
                    window.__arenaMarkerGroup = L.markerClusterGroup({
                        disableClusteringAtZoom: 13,
                        showCoverageOnHover: false,
                        maxClusterRadius: 60,
                        // The full club catalog lands at once — chunked loading
                        // yields to the frame loop so a phone doesn't freeze
                        // while the cluster tree ingests thousands of markers.
                        chunkedLoading: true,
                        iconCreateFunction: function(cluster) {
                            var count = cluster.getChildCount();
                            var d = count < 10 ? 42 : count < 25 ? 46 : count < 100 ? 50 : 56;
                            var label = count > 99 ? '99+' : String(count);
                            return L.divIcon({
                                className: 'arena-cluster',
                                html: '<div class="arena-cluster-disc" style="width:' + d + 'px;height:' + d + 'px">' + label + '</div>',
                                iconSize: [d, d],
                                iconAnchor: [d / 2, d / 2]
                            });
                        }
                    }).addTo(map);
                } else {
                    window.__arenaMarkerGroup = L.layerGroup().addTo(map);
                }

                // Zoomed-out maps shrink the info-card pins — a CSS variable
                // drives scale(var(--arena-pin-scale)) on .arena-pin-wrap.
                // Buckets match the mobile Arena map.
                var applyPinScale = function() {
                    var z = map.getZoom();
                    var s = z >= 17 ? 1.4 : z >= 16 ? 1.2 : z >= 15 ? 1 : z >= 13 ? 0.8
                        : z >= 12 ? 0.6 : z >= 10 ? 0.4 : 0.3;
                    container.style.setProperty('--arena-pin-scale', s);
                };
                map.on('zoom zoomend', applyPinScale);
                applyPinScale();

                setTimeout(function() { map.invalidateSize(); }, 150);
            })();
        """)
    }

    /**
     * Add markers from a JSON array string:
     * `[{id?, lat, lng, title, subtitle?, image?, datetime?, members?, cost?}]`.
     * Each marker gets a dark mini-card popup (photo + info) styled to the app
     * theme (`.arena-map-popup` overrides in AppTheme). When `id` is present,
     * tapping the marker also fires [onMarkerTap] (the Arena map syncs its
     * card strip) and clicking the popup card fires [onMarkerOpen] (open
     * details intent).
     *
     * [cull] limits live markers to the padded viewport (capped at the 250
     * closest to the centre, re-culled on camera moves) — required on phones,
     * where building the full club catalog stalls the main thread for seconds.
     * Desktop keeps the default (false) and renders every marker.
     */
    fun addMarkers(jsonArray: String, cull: Boolean = false) {
        window.asDynamic().__arenaMarkersJson = jsonArray
        window.asDynamic().__arenaCullEnabled = cull

        js("""
            (function() {
                var map = window.__arenaMap;
                var group = window.__arenaMarkerGroup;
                if (!map || !group) return;

                // Titles/addresses are user input — escape before injecting HTML.
                var esc = function(s) {
                    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
                };

                var items = JSON.parse(window.__arenaMarkersJson);

                // Location pin glyph rendered under each info-card (Figma
                // "Map Pin Icon" — teardrop with a punched ring + centre dot).
                var pinSvg = '<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                    '<path d="M18.0002 15.75C19.2428 15.75 20.2502 14.7426 20.2502 13.5C20.2502 12.2574 19.2428 11.25 18.0002 11.25C16.7576 11.25 15.7502 12.2574 15.7502 13.5C15.7502 14.7426 16.7576 15.75 18.0002 15.75Z" fill="#B4CCF6"/>' +
                    '<path d="M18.0003 2.25C11.7974 2.25 6.75034 7.07696 6.75034 13.0078C6.75034 15.8323 8.03777 19.5884 10.5767 24.172C12.6158 27.8522 14.9748 31.1801 16.2017 32.8359C16.4091 33.1189 16.6802 33.349 16.993 33.5076C17.3059 33.6662 17.6517 33.7488 18.0025 33.7488C18.3532 33.7488 18.699 33.6662 19.0119 33.5076C19.3247 33.349 19.5958 33.1189 19.8032 32.8359C21.028 31.1801 23.3891 27.8522 25.4282 24.172C27.9629 19.5898 29.2503 15.8337 29.2503 13.0078C29.2503 7.07696 24.2033 2.25 18.0003 2.25ZM18.0003 18C17.1103 18 16.2403 17.7361 15.5003 17.2416C14.7603 16.7472 14.1835 16.0443 13.8429 15.2221C13.5023 14.3998 13.4132 13.495 13.5868 12.6221C13.7604 11.7492 14.189 10.9474 14.8184 10.318C15.4477 9.68869 16.2495 9.2601 17.1224 9.08647C17.9954 8.91284 18.9002 9.00195 19.7224 9.34255C20.5447 9.68314 21.2475 10.2599 21.742 10.9999C22.2364 11.74 22.5003 12.61 22.5003 13.5C22.499 14.6931 22.0245 15.8369 21.1809 16.6805C20.3373 17.5242 19.1934 17.9987 18.0003 18Z" fill="#B4CCF6"/>' +
                    '</svg>';
                var pinPoint = '<div class="arena-pin-point">' + pinSvg + '</div>';

                // Pre-parse coordinates once and give every item a stable key —
                // the culled renderer diffs live markers against it on each
                // camera move.
                var valid = [];
                items.forEach(function(item) {
                    var lat = parseFloat(item.lat);
                    var lng = parseFloat(item.lng);
                    if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;
                    item.__lat = lat;
                    item.__lng = lng;
                    item.__key = item.id ? String(item.id) : (lat + ',' + lng + ',' + (item.title || ''));
                    valid.push(item);
                });
                window.__arenaAllItems = valid;

                // Build ONE Leaflet marker for an item. Kept as a factory: the
                // renderer below instantiates markers lazily for the culled
                // visible set only — building all ~3000 catalog markers up
                // front stalled phone main threads for many seconds.
                var buildMarker = function(item) {
                    var lat = item.__lat;
                    var lng = item.__lng;

                    // Event items (datetime/image present) render as info-card
                    // pins — photo + date/time + title always visible on the
                    // map, no popup needed (.arena-pin-* styles in AppTheme).
                    // NOTE: clubs are handled FIRST below this check via the
                    // `!item.club` guard — clubs also carry an image, and
                    // falling into this branch would render them as bare
                    // event cards (title only, no rating/meta).
                    if (!item.club && (item.datetime || item.image)) {
                        // Photo IS the card — scrim + text over it; the scrim
                        // and (optional) photo share one background stack.
                        var bg = 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 45%, rgba(0,0,0,0.8) 100%)';
                        if (item.image) {
                            bg += ', url(&quot;' + esc(item.image) + '&quot;)';
                        }
                        var pinHtml = '<div class="arena-pin-wrap">' +
                            '<div class="arena-pin-card" style="background-image:' + bg + '">' +
                            (item.datetime ? '<div class="arena-pin-datetime">' + esc(item.datetime) + '</div>' : '') +
                            (item.title ? '<div class="arena-pin-title">' + esc(item.title) + '</div>' : '') +
                            '</div>' +
                            pinPoint +
                            '</div>';
                        var cardMarker = L.marker([lat, lng], {
                            icon: L.divIcon({ className: 'arena-pin', html: pinHtml, iconSize: [0, 0], iconAnchor: [0, 0] })
                        });
                        if (item.id) {
                            cardMarker.on('click', function() {
                                if (typeof window.__arenaMarkerTapCallback === 'function') {
                                    window.__arenaMarkerTapCallback(String(item.id));
                                }
                                if (typeof window.__arenaMarkerOpenCallback === 'function') {
                                    window.__arenaMarkerOpenCallback(String(item.id));
                                }
                            });
                        }
                        return cardMarker;
                    }

                    // Club items render as an info-card pin mirroring the mobile
                    // club list card: name on top, "location · sport" meta line,
                    // and a rating badge over the photo.
                    if (item.club) {
                        var cbg = 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 45%, rgba(0,0,0,0.8) 100%)';
                        if (item.image) {
                            cbg += ', url(&quot;' + esc(item.image) + '&quot;)';
                        }
                        var meta = [];
                        if (item.subtitle) meta.push(esc(item.subtitle));
                        if (item.sport) meta.push(esc(item.sport));
                        var rating = parseFloat(item.rating);
                        var ratingHtml = (!isNaN(rating) && rating > 0)
                            ? '<div class="arena-club-pin-rating"><span class="arena-club-pin-star">&#9733;</span>' + rating.toFixed(1) + '</div>'
                            : '';
                        var clubHtml = '<div class="arena-pin-wrap">' +
                            '<div class="arena-pin-card" style="background-image:' + cbg + '">' +
                            ratingHtml +
                            (item.title ? '<div class="arena-club-pin-name">' + esc(item.title) + '</div>' : '') +
                            (meta.length > 0 ? '<div class="arena-club-pin-meta">' + meta.join(' &middot; ') + '</div>' : '') +
                            '</div>' +
                            pinPoint +
                            '</div>';
                        var clubMarker = L.marker([lat, lng], {
                            icon: L.divIcon({ className: 'arena-pin', html: clubHtml, iconSize: [0, 0], iconAnchor: [0, 0] })
                        });
                        if (item.id) {
                            clubMarker.on('click', function() {
                                if (typeof window.__arenaMarkerTapCallback === 'function') {
                                    window.__arenaMarkerTapCallback(String(item.id));
                                }
                                if (typeof window.__arenaMarkerOpenCallback === 'function') {
                                    window.__arenaMarkerOpenCallback(String(item.id));
                                }
                            });
                        }
                        return clubMarker;
                    }

                    var marker = L.marker([lat, lng]);

                    // Mini card popup — photo on top, then title / subtitle
                    // (clubs and other id-less markers).
                    var html = '<div style="min-width:200px;max-width:240px">';
                    if (item.image) {
                        html += '<img src="' + esc(item.image) + '" style="display:block;width:100%;height:108px;object-fit:cover" />';
                    }
                    html += '<div style="padding:10px 12px 12px;font-family:inherit">';
                    if (item.datetime) {
                        html += '<div style="font-size:12px;font-weight:600;color:var(--l-text-strong, #ffffff);letter-spacing:0.2px">' + esc(item.datetime) + '</div>';
                    }
                    if (item.title) {
                        html += '<div style="font-size:14px;font-weight:600;color:var(--l-text-strong, #ffffff);margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + esc(item.title) + '</div>';
                    }
                    if (item.subtitle) {
                        html += '<div style="font-size:12px;color:var(--l-popup-sub, #8aa0c8);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + esc(item.subtitle) + '</div>';
                    }
                    var meta = [];
                    if (item.members) meta.push(esc(item.members));
                    if (item.cost) meta.push(esc(item.cost));
                    if (meta.length > 0) {
                        html += '<div style="font-size:12px;font-weight:500;color:var(--arena-text-1);margin-top:8px">' + meta.join(' &middot; ') + '</div>';
                    }
                    html += '</div></div>';

                    var popupHtml;
                    if (item.id) {
                        // Whole card is the "open details" affordance.
                        popupHtml = '<div style="cursor:pointer" onclick="window.__arenaMarkerOpenCallback && window.__arenaMarkerOpenCallback(\'' + esc(String(item.id)) + '\')">' + html + '</div>';
                    } else {
                        popupHtml = html;
                    }
                    marker.bindPopup(popupHtml, {
                        maxWidth: 260,
                        closeButton: false,
                        className: 'arena-map-popup',
                        offset: L.point(0, -6)
                    });
                    if (item.id) {
                        marker.on('click', function() {
                            if (typeof window.__arenaMarkerTapCallback === 'function') {
                                window.__arenaMarkerTapCallback(String(item.id));
                            }
                        });
                    }
                    return marker;
                };

                // Culled, diffed renderer. When culling is on (phones) only the
                // items inside the padded viewport — capped at the closest
                // MAX_PINS to the centre — get real Leaflet markers: building
                // the full ~3000-club catalog up front stalled a phone's main
                // thread for 6+ seconds the moment the map opened. Markers are
                // diffed by key against the live set so camera moves only
                // add/remove the delta (full rebuilds blink every pin).
                // Desktop passes cull=false and renders everything, keeping
                // cluster counts exact — it always handled the full catalog.
                if (!window.__arenaLiveMarkers) window.__arenaLiveMarkers = {};
                var MAX_PINS = 250;
                var render = function() {
                    var m = window.__arenaMap;
                    var g = window.__arenaMarkerGroup;
                    if (!m || !g) return;
                    var all = window.__arenaAllItems || [];
                    var vis = all;
                    if (window.__arenaCullEnabled) {
                        var b = m.getBounds().pad(0.5);
                        vis = all.filter(function(it) { return b.contains([it.__lat, it.__lng]); });
                        if (vis.length > MAX_PINS) {
                            // Over budget (zoomed way out): grid-sample instead
                            // of taking the N closest to the centre — a centre
                            // bias collapsed all of Europe into one lonely
                            // cluster. Bucket by viewport-relative cell, then
                            // round-robin one item per cell until the cap, so
                            // the kept subset spreads across the whole view.
                            var west = b.getWest(), south = b.getSouth();
                            var cw = (b.getEast() - west) / 16 || 1;
                            var ch = (b.getNorth() - south) / 16 || 1;
                            var cells = {};
                            var order = [];
                            vis.forEach(function(it) {
                                var key = Math.floor((it.__lng - west) / cw) + ':' + Math.floor((it.__lat - south) / ch);
                                if (!cells[key]) { cells[key] = []; order.push(key); }
                                cells[key].push(it);
                            });
                            var sampled = [];
                            var round = 0;
                            while (sampled.length < MAX_PINS) {
                                var added = false;
                                for (var i = 0; i < order.length && sampled.length < MAX_PINS; i++) {
                                    var cell = cells[order[i]];
                                    if (round < cell.length) { sampled.push(cell[round]); added = true; }
                                }
                                if (!added) break;
                                round++;
                            }
                            vis = sampled;
                        }
                    }
                    var wanted = {};
                    vis.forEach(function(it) { wanted[it.__key] = it; });
                    var live = window.__arenaLiveMarkers;
                    var toRemove = [];
                    Object.keys(live).forEach(function(k) {
                        if (!wanted[k]) { toRemove.push(live[k]); delete live[k]; }
                    });
                    var toAdd = [];
                    Object.keys(wanted).forEach(function(k) {
                        if (!live[k]) {
                            var mk = buildMarker(wanted[k]);
                            if (mk) { live[k] = mk; toAdd.push(mk); }
                        }
                    });
                    if (toRemove.length > 0) {
                        if (typeof g.removeLayers === 'function') g.removeLayers(toRemove);
                        else toRemove.forEach(function(x) { g.removeLayer(x); });
                    }
                    if (toAdd.length > 0) {
                        if (typeof g.addLayers === 'function') g.addLayers(toAdd);
                        else toAdd.forEach(function(x) { g.addLayer(x); });
                    }
                };
                window.__arenaRenderCulled = render;

                // Re-cull on camera moves (debounced past the pan/zoom inertia).
                if (window.__arenaCullEnabled && !window.__arenaCullBound) {
                    window.__arenaCullBound = true;
                    map.on('moveend zoomend', function() {
                        if (window.__arenaCullTimer) clearTimeout(window.__arenaCullTimer);
                        window.__arenaCullTimer = setTimeout(function() {
                            if (window.__arenaRenderCulled) window.__arenaRenderCulled();
                        }, 120);
                    });
                }

                // Frame the pins once per map instance — marker refreshes (new
                // geolocation, resolved city flags, catalog sync) must not yank
                // the camera away from wherever the user panned it. Bounds come
                // from the raw item list (cheap), not from built markers.
                if (valid.length > 0 && !window.__arenaMarkersFitted) {
                    window.__arenaMarkersFitted = true;
                    var bounds = valid.map(function(it) { return [it.__lat, it.__lng]; });
                    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
                }

                render();
            })();
        """)
    }

    /** Marker tap (receives the marker's `id`) — only markers created with an id fire it. */
    fun onMarkerTap(callback: (String) -> Unit) {
        window.asDynamic().__arenaMarkerTapCallback = callback
    }

    /** Popup mini-card click (receives the marker's `id`) — "open details" intent. */
    fun onMarkerOpen(callback: (String) -> Unit) {
        window.asDynamic().__arenaMarkerOpenCallback = callback
    }

    /** True while a map instance is live — lets callers init once and then
     *  only swap markers on data refreshes (re-init tears down the WebGL
     *  basemap, which is far too heavy to repeat on phones). */
    fun isInitialized(): Boolean =
        js("window.__arenaMap != null").unsafeCast<Boolean>()

    /**
     * Remove all markers from the map.
     */
    fun clearMarkers() {
        js("""
            (function() {
                if (window.__arenaMarkerGroup) {
                    window.__arenaMarkerGroup.clearLayers();
                }
                window.__arenaLiveMarkers = {};
                window.__arenaAllItems = [];
            })();
        """)
    }

    fun destroy() {
        js("""
            (function() {
                if (window.__arenaMarkerGroup) {
                    try { window.__arenaMarkerGroup.clearLayers(); } catch(e) {}
                    window.__arenaMarkerGroup = null;
                }
                if (window.__arenaMap) {
                    try { window.__arenaMap.remove(); } catch(e) {}
                    window.__arenaMap = null;
                }
                window.__arenaMapMoveCallback = null;
                window.__arenaMarkerTapCallback = null;
                window.__arenaMarkerOpenCallback = null;
                window.__arenaMapSkipMove = false;
                window.__arenaMarkersFitted = false;
                window.__arenaLiveMarkers = null;
                window.__arenaAllItems = null;
                window.__arenaRenderCulled = null;
                window.__arenaCullBound = false;
                window.__arenaCullEnabled = false;
                if (window.__arenaCullTimer) { clearTimeout(window.__arenaCullTimer); window.__arenaCullTimer = null; }
            })();
        """)
    }
}
